import React, { useState, useEffect } from 'react';
import Modal from '../../../shared/components/ui/Modal';
import Button from '../../../shared/components/ui/Button';
import SearchableDropdown from '../../../shared/components/ui/SearchableDropdown';
import Toggle from '../../../shared/components/ui/Toggle';
import { useSeasonsByTournament } from '../../../shared/hooks/useSeasons';
import { useMatchPointsOperations } from '../../../shared/hooks/useMatchPoints';
import { TournamentStandingsConfig, CreateOrUpdateTournamentStandingsConfig } from '../../../shared/services/matchPointsService';

interface TournamentConfigModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  tournamentId: string;
  initialConfig?: TournamentStandingsConfig | null;
}

const formatTypeOptions = [
  { id: 'league', name: 'League' },
  { id: 'knockout', name: 'Knockout' },
  { id: 'group_stage', name: 'Group Stage' },
];

const TournamentConfigModal: React.FC<TournamentConfigModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  tournamentId,
  initialConfig,
}) => {
  const [formData, setFormData] = useState({
    id: '',
    season_id: '',
    format_type: 'league',
    stage_name: '',
    default_win_points: 3,
    default_draw_points: 1,
    default_loss_points: 0,
    is_active: true,
  });
  const [error, setError] = useState<string | null>(null);

  const { seasons, loading: seasonsLoading } = useSeasonsByTournament(tournamentId);
  const { createOrUpdateStandingsConfig, loading: saveLoading } = useMatchPointsOperations();

  // Populate form data when modal opens
  useEffect(() => {
    if (isOpen) {
      if (initialConfig) {
        setFormData({
          id: initialConfig.id,
          season_id: initialConfig.season_id || '',
          format_type: initialConfig.format_type,
          stage_name: initialConfig.stage_name || '',
          default_win_points: initialConfig.default_win_points,
          default_draw_points: initialConfig.default_draw_points,
          default_loss_points: initialConfig.default_loss_points,
          is_active: initialConfig.is_active,
        });
      } else {
        // Reset form for new config
        setFormData({
          id: '',
          season_id: '',
          format_type: 'league',
          stage_name: '',
          default_win_points: 3,
          default_draw_points: 1,
          default_loss_points: 0,
          is_active: true,
        });
      }
      setError(null);
    }
  }, [isOpen, initialConfig]);

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSaveConfig = async () => {
    setError(null);

    try {
      const configData: CreateOrUpdateTournamentStandingsConfig = {
        tournament_id: tournamentId,
        season_id: formData.season_id || undefined,
        format_type: formData.format_type,
        stage_name: formData.stage_name || undefined,
        default_win_points: formData.default_win_points,
        default_draw_points: formData.default_draw_points,
        default_loss_points: formData.default_loss_points,
        is_active: formData.is_active,
      };

      // Only include id if we're editing an existing config
      if (formData.id) {
        configData.id = formData.id;
      }

      await createOrUpdateStandingsConfig(configData);
      
      onSuccess();
    } catch (err: any) {
      console.error('Failed to save config:', err);
      setError(err.message || 'Failed to save configuration');
    }
  };

  const modalTitle = initialConfig ? 'Edit Tournament Configuration' : 'Create Tournament Configuration';

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={modalTitle}>
      <div className="p-6">
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-md">
            <p className="text-red-600 text-sm">{error}</p>
          </div>
        )}

        <div className="space-y-6">
          {/* Season */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Season
            </label>
            <SearchableDropdown
              value={formData.season_id}
              onChange={(value) => handleInputChange('season_id', value)}
              options={seasons.map(season => ({
                id: season.id,
                name: season.name
              }))}
              placeholder="Select season (optional)"
              className="w-full"
              loading={seasonsLoading}
            />
          </div>

          {/* Format Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Tournament Format
            </label>
            <SearchableDropdown
              value={formData.format_type}
              onChange={(value) => handleInputChange('format_type', value)}
              options={formatTypeOptions}
              placeholder="Select format"
              className="w-full"
            />
          </div>

          {/* Stage Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Stage Name
            </label>
            <input
              type="text"
              value={formData.stage_name}
              onChange={(e) => handleInputChange('stage_name', e.target.value)}
              placeholder="e.g., Group A, Quarter Finals"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          {/* Default Scoring Rules */}
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Win Points
              </label>
              <input
                type="number"
                value={formData.default_win_points}
                onChange={(e) => handleInputChange('default_win_points', parseInt(e.target.value) || 0)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Draw Points
              </label>
              <input
                type="number"
                value={formData.default_draw_points}
                onChange={(e) => handleInputChange('default_draw_points', parseInt(e.target.value) || 0)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Loss Points
              </label>
              <input
                type="number"
                value={formData.default_loss_points}
                onChange={(e) => handleInputChange('default_loss_points', parseInt(e.target.value) || 0)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>

          {/* Active Configuration */}
          <div>
            <Toggle
              checked={formData.is_active}
              onChange={(checked) => handleInputChange('is_active', checked)}
              label="Set as active configuration"
              size="md"
            />
            <p className="text-xs text-gray-500 mt-2">
              Multiple configurations can be active for the same tournament
            </p>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="mt-8 flex justify-end space-x-3">
          <Button
            onClick={onClose}
            variant="ghost"
            size="md"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSaveConfig}
            variant="primary"
            size="md"
          >
            {saveLoading ? 'Saving...' : (initialConfig ? 'Save Changes' : 'Create Configuration')}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default TournamentConfigModal;
